/**
 * URLs of the APIs loaded by the browser itself (images of HTML contents, new tabs), for the cases where no header can be sent.
 *
 * Without session seal, the token of the session is put in the URL, as always. With the session seal (see sessionSeal.js),
 * the token of the session can't be used without proof:
 * - the images are loaded through axios (which adds the proof) and shown from a blob URL, like the other authenticated images,
 * - the new tabs get a link token instead, limited to the path and a few minutes (only allowed for the scripts and lists of the admins).
 */
import { getAuthorizationToken, getHeaderAuthorization } from "@/_services/axios"
import { getLinkToken, hasSessionSeal } from "@/_services/sessionSeal"
import axios from "axios"

const tokenSourceAttribute = "data-token-src"

let imagesObserver

function addToken(url, token) {
  return `${url}${url.includes("?") ? "&" : "?"}token=${token}`
}

/**
 * Opens the URL with its token in a new tab, returns the window.
 */
export function openTokenUrl(url) {
  if (!hasSessionSeal()) return window.open(addToken(url, getHeaderAuthorization()?.substring("bearer ".length)), "_blank")

  // Opened at once: a window opened after waiting for the link token would be blocked as a popup
  const win = window.open("", "_blank")
  getLinkToken(url)
    .then(linkToken => {
      if (win) win.location.href = addToken(url, linkToken)
    })
    .catch(() => win?.close())
  return win
}

async function loadImageSource(img, url) {
  const { data } = await axios.get(url, { responseType: "blob", skipSpinner: true })
  const blobUrl = URL.createObjectURL(data)
  // The image keeps its pixels once loaded: the blob can be freed
  img.addEventListener("load", () => URL.revokeObjectURL(blobUrl), { once: true })
  img.setAttribute("src", blobUrl)
}

function setImageSource(img) {
  const url = img.getAttribute(tokenSourceAttribute)
  img.removeAttribute(tokenSourceAttribute)
  // Not loaded (deleted document, no permission...): the image stays empty, like an image whose URL fails
  loadImageSource(img, url).catch(() => {})
}

function setImagesSources(node) {
  if (node.nodeType !== 1) return
  if (node.matches(`img[${tokenSourceAttribute}]`)) setImageSource(node)
  node.querySelectorAll(`img[${tokenSourceAttribute}]`).forEach(setImageSource)
}

// The images to load get their source once in the page, whoever puts them there (dangerouslySetInnerHTML...)
function observeImages() {
  if (imagesObserver || typeof MutationObserver === "undefined") return

  imagesObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) mutation.addedNodes.forEach(setImagesSources)
  })
  imagesObserver.observe(document.documentElement, { childList: true, subtree: true })
}

/**
 * The source attribute of an image of an HTML string: src="url?token=..." without session seal,
 * otherwise an attribute replaced by the src once the image is in the page and loaded (see loadImageSource).
 */
export function getTokenImageSourceAttribute(url) {
  if (!hasSessionSeal()) return `src="${addToken(url, getAuthorizationToken())}"`

  observeImages()
  return `${tokenSourceAttribute}="${url}"`
}

/**
 * Sets the source of an image element of a document being built (DOMParser...), see getTokenImageSourceAttribute.
 */
export function setTokenImageSource(img, url) {
  if (!hasSessionSeal()) {
    img.setAttribute("src", addToken(url, getAuthorizationToken()))
    return
  }

  observeImages()
  img.setAttribute(tokenSourceAttribute, url)
}
